// =============================================================================
// Planilha orçamentária — o editor do orçamento
//
// O orçado × realizado com drilldown mora na aba "Orçado vs Realizado" da
// Visão financeira (OrcadoRealizado.tsx); aqui é só o orçado, editável. Desde
// 21/10 a grade usa as peças comuns de `Grade.tsx`, como a do Orçado vs
// Realizado: cabeçalho e total congelados, rolagem lateral sempre à vista,
// coluna dos nomes ajustável, centro de custo que abre e fecha, filtros em
// lista suspensa numa barra compacta que recolhe, e linhas sem valor nos meses
// da tela sempre ocultas fora da edição (houve uma caixa para mostrá-las, que
// saiu no mesmo dia, a pedido do Financeiro).
// =============================================================================

// =============================================================================
// Editor do orçamento — o ano inteiro, como o Financeiro conhece do Excel
//
// Pedido de 18/09: ver o orçamento com centro de custo e categoria nas linhas e
// os doze meses nas colunas, e editar ali mesmo. Até aqui só dava para
// cadastrar uma linha por vez ou importar colando do Excel.
//
// Quem decide o que pode é a RPC da migration 032, não esta tela:
//   rascunho (2027) ... edição livre
//   vigente  (2026) ... edição com justificativa obrigatória (AF-01 §7)
//   encerrado ......... só leitura
// e linha com realizado casado não sai — reduz-se o valor. A tela só evita
// oferecer o que a RPC recusaria, e mostra a recusa dela quando vier.
//
// As alterações se acumulam e vão juntas num salvamento: uma transação, uma
// justificativa, um registro no Histórico.
// =============================================================================

import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, buscarRealizadoPorFornecedor, FalhaApi } from "../lib/api.ts";
import { moeda } from "../lib/formato.ts";
import { deCentavos, paraCentavos } from "../lib/dinheiro.ts";
import {
  Painel,
  Pilula,
  Vazio,
} from "../componentes/ui.tsx";
import { FiltroAno, FiltroMes, resumoPeriodo } from "../componentes/FiltroPeriodo.tsx";
import {
  AbrirFecharTodos,
  AlcaLargura,
  BotaoGrupo,
  CaixaGrade,
  CampoCompacto,
  CLASSE_BOTAO_BARRA,
  FiltrosRecolhiveis,
  NomeNaLargura,
  Seletor,
  useLarguraColuna,
  useRecolhidos,
} from "../componentes/Grade.tsx";
import { baixarCsv, centavosParaCsv } from "../lib/csv.ts";
import { Modal } from "../componentes/Modal.tsx";
import { ModalRenovacao, dataRenovacao, type AlvoRenovacao } from "./ModalRenovacao.tsx";

/** `fornecedor_*`: a pessoa ou fornecedor ligado à subcategoria (080). */
type Sub = { id: string; nome: string; valor_centavos: string; fornecedor_id?: string | null; fornecedor_nome?: string | null };
type Linha = {
  linha_id: string;
  centro_custo_id: string;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  categoria_id: string;
  categoria_codigo: string | null;
  categoria_nome: string;
  competencia: string;
  tipo: string;
  valor_centavos: string;
  subcategorias: Sub[];
  /** 'despesa' | 'investimento' | 'receita' — migration 043. */
  natureza: string;
};
type Renovacao = {
  id: string; centro_custo_id: string; categoria_id: string; subcategoria: string | null;
  proxima_renovacao: string; observacao: string | null;
};
type Resposta = {
  ano: number; exercicios: { ano: number; status: string }[]; linhas: Linha[];
  /** Migration 050. Opcional: a API antiga não manda. */
  renovacoes?: Renovacao[];
};

/** Nome comparado como o banco compara (fn_normalizar_nome): sem acento, sem caixa. */
const normalizar = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
type Cadastros = {
  centros: { id: string; codigo: string; nome: string }[];
  categorias: { id: string; codigo: string | null; nome: string; natureza: string; pai_id: string | null }[];
  fornecedores: Pessoa[];
};
/** Pessoa ou fornecedor do Conta Azul (o cadastro de fornecedores). */
type Pessoa = { id: string; nome: string | null };
/** Subcategoria acrescentada nesta edição; `pessoa` é ligada depois de salvar. */
type SubExtra = { linha: string; nome: string; pessoa?: Pessoa };

type Alteracao = {
  acao: "valor" | "subcategoria";
  linha_id?: string | null;
  despesa_id?: string | null;
  centro_custo_id: string;
  categoria_id: string;
  competencia: string;
  tipo?: string;
  nome?: string;
  valor_centavos: string | null;
};
type Pendente = { alteracao: Alteracao; original: string | null };

type CelulaSub = { despesa: Sub; linha: Linha } | null;
type LinhaSub = { chave: string; nome: string; meses: CelulaSub[]; duplicada: Set<number>; pessoa: Pessoa | null };
type LinhaCat = {
  chave: string;
  cc: { id: string; codigo: string; nome: string };
  cat: { id: string; codigo: string | null; nome: string };
  tipo: string;
  meses: Linha[][];
  subs: LinhaSub[];
};

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const STATUS: Record<string, { rotulo: string; tom: "ok" | "atencao" | "neutro"; regra: string }> = {
  rascunho: { rotulo: "Rascunho", tom: "ok", regra: "Edição livre." },
  vigente: { rotulo: "Vigente", tom: "atencao", regra: "Alterações exigem justificativa, que fica no Histórico." },
  encerrado: { rotulo: "Encerrado", tom: "neutro", regra: "Somente leitura." },
};

const somar = (vs: (string | null | undefined)[]) =>
  vs.reduce<bigint>((a, v) => a + (v ? BigInt(v) : 0n), 0n).toString();
const comp = (ano: number, m: number) => `${ano}-${String(m + 1).padStart(2, "0")}-01`;

/**
 * O que a pessoa digitou numa célula. Vazio ou zero = tirar.
 *
 * Até 18/09 havia aqui uma trava que recusava vírgula com 3+ casas, porque o
 * `paraCentavos` lia "411,3333333" como milhar (R$ 4 bi). O conversor foi
 * corrigido na raiz no mesmo dia — 4+ casas são decimal arredondado — e a trava
 * saiu, para a Planilha, a Importação e o Cadastro lerem valor do mesmo jeito.
 */
function lerCelula(texto: string): { ok: true; centavos: string | null } | { ok: false } {
  const t = texto.trim();
  if (t === "") return { ok: true, centavos: null };
  const c = paraCentavos(t);
  if (c === null || BigInt(c) < 0n) return { ok: false };
  return { ok: true, centavos: BigInt(c) === 0n ? null : c };
}

export function PlanilhaOrcamento({
  periodo,
  onMudarPeriodo,
}: {
  periodo: string[];
  onMudarPeriodo: (p: string[]) => void;
}) {
  const qc = useQueryClient();

  // O ano vem do filtro, não de um estado próprio: é o campo "Ano" do
  // protótipo, e ele é o mesmo das outras telas.
  const ano = Number(periodo[periodo.length - 1]!.slice(0, 4));

  // "Visão" do protótipo: o mês selecionado, ou o ano inteiro mês a mês. Ela
  // não muda o que se edita — muda quantas COLUNAS aparecem. Com 428 linhas e
  // doze meses, ver só o mês em que se está mexendo é a diferença entre rolar
  // na horizontal e não rolar.
  const [visao, setVisao] = useState<"mes" | "ano">("ano");

  // ⚠️ Despesa e receita NUNCA aparecem na mesma grade, e o motivo não é
  // organização: a coluna "Total do ano" somaria entrada com saída e
  // devolveria um número que não é nem uma coisa nem outra. O lado "Despesa"
  // é `natureza <> 'receita'` — despesa E investimento juntos, exatamente o
  // que a planilha mostrava antes da 043, para o comportamento de hoje não
  // mudar para quem não orça receita.
  const [natureza, setNatureza] = useState<"despesa" | "receita">("despesa");

  const [ccFiltro, setCcFiltro] = useState("todos");
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const [editando, setEditando] = useState(false);
  const [pendentes, setPendentes] = useState<Map<string, Pendente>>(new Map());
  const [invalidas, setInvalidas] = useState<Map<string, string>>(new Map());
  const [extras, setExtras] = useState<{ cc: string; cat: string; tipo: string }[]>([]);
  const [subsExtras, setSubsExtras] = useState<SubExtra[]>([]);
  const [adicionando, setAdicionando] = useState(false);
  const [novaSubEm, setNovaSubEm] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [renovando, setRenovando] = useState<AlvoRenovacao | null>(null);
  const [renomeando, setRenomeando] = useState<{ r: LinhaCat; s: LinhaSub } | null>(null);
  const [ligando, setLigando] = useState<{ r: LinhaCat; s: LinhaSub } | null>(null);
  const coluna = useLarguraColuna("planilha");
  const recolhidos = useRecolhidos();

  const eu = useQuery({
    queryKey: ["eu"],
    queryFn: () => api.get<{ papeis: string[] }>("/auth/eu"),
    staleTime: 5 * 60_000,
  });
  const podeEditarPapel = (eu.data?.papeis ?? []).some((p) => p === "financeiro" || p === "admin");

  const q = useQuery({
    queryKey: ["planilha", ano],
    queryFn: () => api.get<Resposta>(`/api/planilha?ano=${ano}`),
  });
  const cadastros = useQuery({
    queryKey: ["cadastros"],
    queryFn: () => api.get<Cadastros>("/api/cadastros"),
    enabled: editando,
  });

  // Os índices de mês que a grade desenha. O modelo continua com os doze —
  // subtotal, total da linha e o que se salva não mudam com a visão; o que
  // muda é o que se vê.
  const colunas = useMemo(
    () =>
      visao === "ano"
        ? MESES.map((_, i) => i)
        : periodo
            .filter((c) => c.slice(0, 4) === String(ano))
            .map((c) => Number(c.slice(5, 7)) - 1)
            .sort((x, y) => x - y),
    [visao, periodo, ano],
  );

  // Renovação por contrato (050): CC + categoria + subcategoria (nula = a linha).
  const renovacoes = useMemo(() => {
    const m = new Map<string, Renovacao>();
    for (const r of q.data?.renovacoes ?? []) {
      m.set(`${r.centro_custo_id}|${r.categoria_id}|${r.subcategoria ? normalizar(r.subcategoria) : ""}`, r);
    }
    return m;
  }, [q.data]);
  const renovacaoDe = (r: LinhaCat, sub: string | null) =>
    renovacoes.get(`${r.cc.id}|${r.cat.id}|${sub ? normalizar(sub) : ""}`);
  const abrirRenovacao = (r: LinhaCat, sub: string | null) => {
    const reg = renovacaoDe(r, sub);
    setRenovando({
      centro_custo_id: r.cc.id, categoria_id: r.cat.id, subcategoria: sub,
      rotulo: `${r.cc.codigo} ${r.cc.nome} · ${r.cat.nome}${sub ? ` › ${sub}` : ""}`,
      proxima_renovacao: reg?.proxima_renovacao ?? null, observacao: reg?.observacao ?? null,
      registrado: !!reg,
    });
  };

  const status = q.data?.exercicios.find((e) => e.ano === ano)?.status;
  const podeEditar = podeEditarPapel && !!status && status !== "encerrado";

  // ── o modelo da grade: CC → categoria → subcategorias, doze meses cada ──
  const grade = useMemo(() => {
    const porCc = new Map<string, LinhaCat[]>();
    const porChave = new Map<string, LinhaCat>();
    const nova = (cc: LinhaCat["cc"], cat: LinhaCat["cat"], tipo: string) => {
      const chave = `${cc.id}|${cat.id}`;
      let r = porChave.get(chave);
      if (!r) {
        r = { chave, cc, cat, tipo, meses: MESES.map(() => []), subs: [] };
        porChave.set(chave, r);
        if (!porCc.has(cc.id)) porCc.set(cc.id, []);
        porCc.get(cc.id)!.push(r);
      }
      return r;
    };
    const subDe = (r: LinhaCat, nome: string) => {
      const chave = `${r.chave}|${nome.trim().toLowerCase()}`;
      let s = r.subs.find((x) => x.chave === chave);
      if (!s) {
        s = { chave, nome: nome.trim(), meses: MESES.map(() => null), duplicada: new Set(), pessoa: null };
        r.subs.push(s);
      }
      return s;
    };

    for (const l of q.data?.linhas ?? []) {
      if ((l.natureza === "receita") !== (natureza === "receita")) continue;
      const r = nova(
        { id: l.centro_custo_id, codigo: l.centro_custo_codigo, nome: l.centro_custo_nome },
        { id: l.categoria_id, codigo: l.categoria_codigo, nome: l.categoria_nome },
        l.tipo,
      );
      const m = Number(l.competencia.slice(5, 7)) - 1;
      r.meses[m]!.push(l);
      for (const d of l.subcategorias) {
        const s = subDe(r, d.nome);
        if (d.fornecedor_id && !s.pessoa) s.pessoa = { id: d.fornecedor_id, nome: d.fornecedor_nome ?? null };
        if (s.meses[m]) s.duplicada.add(m);
        else s.meses[m] = { despesa: d, linha: l };
      }
    }

    // Linhas e subcategorias acrescentadas nesta edição, ainda sem valor salvo.
    const ccs = new Map((cadastros.data?.centros ?? []).map((c) => [c.id, c]));
    const cats = new Map((cadastros.data?.categorias ?? []).map((c) => [c.id, c]));
    for (const x of extras) {
      const cc = ccs.get(x.cc), cat = cats.get(x.cat);
      if (!cc || !cat) continue;
      if ((cat.natureza === "receita") !== (natureza === "receita")) continue;
      nova(cc, { id: cat.id, codigo: cat.codigo, nome: cat.nome }, x.tipo);
    }
    for (const x of subsExtras) {
      const r = porChave.get(x.linha);
      if (!r) continue;
      const s = subDe(r, x.nome);
      if (x.pessoa && !s.pessoa) s.pessoa = x.pessoa;
    }

    const ordemCat = (a: LinhaCat, b: LinhaCat) =>
      (a.cat.codigo ?? "~").localeCompare(b.cat.codigo ?? "~", undefined, { numeric: true })
      || a.cat.nome.localeCompare(b.cat.nome);
    return [...porCc.values()]
      .map((rs) => rs.sort(ordemCat))
      .sort((a, b) => a[0]!.cc.codigo.localeCompare(b[0]!.cc.codigo, undefined, { numeric: true }));
  }, [q.data, extras, subsExtras, cadastros.data, natureza]);

  const termo = "";
  const doCc = grade.filter((rs) => ccFiltro === "todos" || rs[0]!.cc.id === ccFiltro);

  // ── valores exibidos: o salvo, ou o que está pendente ──
  const chaveValor = (r: LinhaCat, m: number) => `v|${r.chave}|${m}`;
  const chaveSub = (s: LinhaSub, m: number) => `s|${s.chave}|${m}`;
  const subsPendentesNoMes = (r: LinhaCat, m: number) => r.subs.some((s) => pendentes.has(chaveSub(s, m)));

  const valorSub = (s: LinhaSub, m: number): string | null => {
    const p = pendentes.get(chaveSub(s, m));
    return p ? p.alteracao.valor_centavos : s.meses[m]?.despesa.valor_centavos ?? null;
  };
  const valorCat = (r: LinhaCat, m: number): string | null => {
    const ls = r.meses[m]!;
    if (ls.length > 1) return somar(ls.map((l) => l.valor_centavos));
    const l = ls[0];
    if ((l?.subcategorias.length ?? 0) > 0 || subsPendentesNoMes(r, m)) {
      const plano = l && l.subcategorias.length === 0 ? valorPlano(r, m) : null;
      const total = BigInt(somar([...r.subs.map((s) => valorSub(s, m)), plano]));
      return total === 0n ? null : total.toString();
    }
    return valorPlano(r, m);
  };
  function valorPlano(r: LinhaCat, m: number): string | null {
    const p = pendentes.get(chaveValor(r, m));
    return p ? p.alteracao.valor_centavos : r.meses[m]![0]?.valor_centavos ?? null;
  }

  // Linhas sem valor nos meses da tela saem (pedido de 20/10, item 1.3) — mas
  // nunca na edição, que é justamente quando se preenche o mês vazio.
  // ⚠️ Depois de `valorCat`, e não antes: ela é `const`, e chamá-la antes da
  // declaração estoura na hora de desenhar, sem o compilador avisar.
  const ocultarVazias = !editando;
  const temValor = (r: LinhaCat) => colunas.some((m) => valorCat(r, m) !== null);
  const visiveis = doCc
    .map((rs) => rs.filter((r) => (!termo
      || `${r.cat.codigo ?? ""} ${r.cat.nome}`.toLowerCase().includes(termo)
      || r.subs.some((s) => s.nome.toLowerCase().includes(termo)))
      && (!ocultarVazias || temValor(r))))
    .filter((rs) => rs.length > 0);

  const editavelCat = (r: LinhaCat, m: number) => {
    const ls = r.meses[m]!;
    return podeEditar && editando && ls.length <= 1
      && (ls[0]?.subcategorias.length ?? 0) === 0 && !subsPendentesNoMes(r, m);
  };
  const editavelSub = (r: LinhaCat, s: LinhaSub, m: number) =>
    podeEditar && editando && r.meses[m]!.length <= 1 && !s.duplicada.has(m);

  // ── registrar o que foi digitado ──
  const registrar = (chave: string, texto: string, montar: (centavos: string | null) => Pendente) => {
    const lido = lerCelula(texto);
    setInvalidas((atual) => {
      const n = new Map(atual);
      if (lido.ok) n.delete(chave); else n.set(chave, texto);
      return n;
    });
    if (!lido.ok) return;
    const p = montar(lido.centavos);
    setPendentes((atual) => {
      const n = new Map(atual);
      if (p.alteracao.valor_centavos === p.original) n.delete(chave); else n.set(chave, p);
      return n;
    });
  };
  const digitarCat = (r: LinhaCat, m: number, texto: string) => {
    const l = r.meses[m]![0];
    registrar(chaveValor(r, m), texto, (centavos) => ({
      original: l?.valor_centavos ?? null,
      alteracao: {
        acao: "valor", linha_id: l?.linha_id ?? null, centro_custo_id: r.cc.id,
        categoria_id: r.cat.id, competencia: comp(ano, m), tipo: r.tipo, valor_centavos: centavos,
      },
    }));
  };
  const digitarSub = (r: LinhaCat, s: LinhaSub, m: number, texto: string) => {
    const c = s.meses[m];
    const l = r.meses[m]![0];
    registrar(chaveSub(s, m), texto, (centavos) => ({
      original: c?.despesa.valor_centavos ?? null,
      alteracao: {
        acao: "subcategoria", despesa_id: c?.despesa.id ?? null, linha_id: l?.linha_id ?? null,
        centro_custo_id: r.cc.id, categoria_id: r.cat.id, competencia: comp(ano, m), tipo: r.tipo,
        nome: s.nome, valor_centavos: centavos,
      },
    }));
  };
  const zerarLinha = (r: LinhaCat) => {
    MESES.forEach((_, m) => {
      if (r.subs.length > 0) r.subs.forEach((s) => { if (valorSub(s, m) && editavelSub(r, s, m)) digitarSub(r, s, m, ""); });
      else if (valorPlano(r, m) && editavelCat(r, m)) digitarCat(r, m, "");
    });
  };

  const descartar = () => {
    setPendentes(new Map()); setInvalidas(new Map()); setExtras([]); setSubsExtras([]);
  };

  // Renomear (076, pedido da Mariana em 27/10): a subcategoria nos doze meses,
  // salva na hora, fora do "Salvar…". Com alteração pendente, avisa em vez de
  // abrir: o pendente da subcategoria é guardado pelo nome, e a grade o
  // perderia de vista depois de recarregar.
  const abrirRenomear = (r: LinhaCat, s: LinhaSub) => {
    if (pendentes.size > 0 || invalidas.size > 0) {
      setAviso("Salve ou descarte as alterações antes de renomear uma subcategoria.");
      return;
    }
    setAviso(null);
    setRenomeando({ r, s });
  };
  // Ligar a pessoa (080): a mesma guarda do renomear. A subcategoria que só
  // existe na tela guarda a pessoa e é ligada depois de salvar.
  const abrirPessoa = (r: LinhaCat, s: LinhaSub) => {
    const soNaTela = s.meses.every((c) => c === null);
    if (!soNaTela && (pendentes.size > 0 || invalidas.size > 0)) {
      setAviso("Salve ou descarte as alterações antes de ligar uma pessoa à subcategoria.");
      return;
    }
    setAviso(null);
    setLigando({ r, s });
  };
  const pessoaExtra = (r: LinhaCat, s: LinhaSub, pessoa: Pessoa | undefined) =>
    setSubsExtras((x) => x.map((e) =>
      e.linha === r.chave && e.nome.trim().toLowerCase() === s.nome.toLowerCase() ? { ...e, pessoa } : e));
  // Depois de salvar, liga as subcategorias novas que nasceram com pessoa.
  // A que ficou sem valor não foi criada, e não há o que ligar.
  const ligarExtras = async (): Promise<string[]> => {
    const falhas: string[] = [];
    for (const x of subsExtras) {
      if (!x.pessoa) continue;
      const [cc, cat] = x.linha.split("|");
      try {
        await api.post("/api/planilha/vincular-subcategoria", {
          exercicio_ano: ano, centro_custo_id: cc, categoria_id: cat, nome: x.nome, fornecedor_id: x.pessoa.id,
        });
      } catch (e) {
        if (!/não existe/.test((e as FalhaApi).message)) falhas.push(`${x.nome}: ${(e as FalhaApi).message}`);
      }
    }
    return falhas;
  };
  // A subcategoria acrescentada nesta edição e ainda sem valor muda só aqui.
  const renomearExtra = (r: LinhaCat, s: LinhaSub, nome: string) =>
    setSubsExtras((x) => x.map((e) =>
      e.linha === r.chave && e.nome.trim().toLowerCase() === s.nome.toLowerCase() ? { ...e, nome } : e));
  // Trocar de exercício com alteração pendente perderia o que foi digitado:
  // avisa, em vez de descartar em silêncio ou abrir `confirm()` do navegador.
  const trocarAno = (novoPeriodo: string[]) => {
    if (pendentes.size > 0) {
      setAviso("Salve ou descarte as alterações antes de trocar de exercício.");
      return;
    }
    descartar();
    onMudarPeriodo(novoPeriodo);
  };

  const alternar = (chave: string) =>
    setAbertas((a) => { const n = new Set(a); if (n.has(chave)) n.delete(chave); else n.add(chave); return n; });

  const totalGeral = MESES.map((_, m) =>
    somar(visiveis.flatMap((rs) => rs.map((r) => valorCat(r, m)))));

  // Exporta o que está filtrado na tela: as colunas visíveis, o total do ano,
  // e uma linha por subcategoria logo abaixo da categoria dela.
  function exportar() {
    const cab = ["Centro de custo", "Categoria", "Subcategoria", ...colunas.map((m) => `${MESES[m]}/${ano}`), "Total do ano"];
    const linhas = [cab];
    for (const rs of visiveis) {
      for (const r of rs) {
        const cc = `${r.cc.codigo} · ${r.cc.nome}`;
        const cat = `${r.cat.codigo ? r.cat.codigo + " " : ""}${r.cat.nome}`;
        const vs = MESES.map((_, m) => valorCat(r, m));
        linhas.push([cc, cat, "", ...colunas.map((m) => centavosParaCsv(vs[m])), centavosParaCsv(somar(vs))]);
        for (const sb of r.subs) {
          const ss = MESES.map((_, m) => valorSub(sb, m));
          linhas.push([cc, cat, sb.nome, ...colunas.map((m) => centavosParaCsv(ss[m])), centavosParaCsv(somar(ss))]);
        }
      }
    }
    const ccSufixo = ccFiltro === "todos" ? "" : `_${visiveis[0]?.[0]?.cc.codigo ?? ""}`;
    baixarCsv(`planilha_orcamentaria_${ano}${ccSufixo}_${natureza}.csv`, linhas);
  }

  return (
    <div className="pagina pb-28">
      <header className="pt-7 pb-5">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">
          Operacional
        </p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
          Planilha orçamentária
        </h1>
        <p className="mt-1 text-[14px] text-tinta-fraca">
          O ano inteiro por centro de custo e categoria. Clique na seta para ver as subcategorias.
        </p>
      </header>

      {/* Os quatro campos do protótipo — Visão, Mês, Ano e Centro de custo —,
          mais a Natureza. As ações (editar, exportar, abrir e fechar) ficam à
          vista mesmo com os filtros recolhidos. */}
      <FiltrosRecolhiveis
        chave="planilha"
        resumo={[
          natureza === "receita" ? "Receita" : "Despesa e investimento",
          visao === "ano" ? `Ano completo de ${ano}` : resumoPeriodo(periodo),
          ccFiltro !== "todos" ? grade.find((rs) => rs[0]!.cc.id === ccFiltro)?.[0]?.cc.nome : null,
        ].filter(Boolean).join(" · ")}
        acoes={
          <>
            {status && (
              <span title={podeEditarPapel ? STATUS[status]?.regra : undefined}>
                <Pilula tom={STATUS[status]?.tom ?? "neutro"}>
                  {STATUS[status]?.rotulo ?? status}
                </Pilula>
              </span>
            )}
            <AbrirFecharTodos grupos={visiveis.map((rs) => rs[0]!.cc.id)} recolhidos={recolhidos} rotulo="centros de custo" />
            <button
              type="button"
              onClick={exportar}
              disabled={visiveis.length === 0 || pendentes.size > 0}
              title={pendentes.size > 0 ? "Salve ou descarte as alterações antes de exportar" : undefined}
              className={CLASSE_BOTAO_BARRA}
            >
              Exportar CSV
            </button>
            {podeEditar && !editando && (
              <button
                type="button"
                onClick={() => {
                  setAviso(null);
                  setEditando(true);
                }}
                className="rounded-lg bg-laranja px-3 py-1.5 text-[12.5px] font-bold text-azul"
              >
                Editar orçamento
              </button>
            )}
            {editando && (
              <>
                <Botao onClick={() => setAdicionando(true)}>+ Adicionar linha</Botao>
                <Botao
                  onClick={() => {
                    descartar();
                    setEditando(false);
                  }}
                >
                  Sair da edição
                </Botao>
              </>
            )}
          </>
        }
      >
        <CampoCompacto rotulo="Natureza">
          <Seletor rotulo="Natureza" valor={natureza} onMudar={setNatureza}
            opcoes={[["despesa", "Despesa e investimento"], ["receita", "Receita"]] as const} />
        </CampoCompacto>

        <CampoCompacto rotulo="Visão">
          <Seletor rotulo="Visão" valor={visao} onMudar={setVisao}
            opcoes={[["mes", "Mês selecionado"], ["ano", "Ano completo"]] as const} />
        </CampoCompacto>

        <CampoCompacto rotulo="Mês">
          <FiltroMes compacto periodo={periodo} onMudar={onMudarPeriodo} />
        </CampoCompacto>

        <CampoCompacto rotulo="Ano">
          {/* ⚠️ Com alteração pendente, trocar de exercício AVISA em vez de
              descartar — a guarda que o seletor antigo já tinha. */}
          <FiltroAno
            compacto
            periodo={periodo}
            onMudar={trocarAno}
            exercicios={(q.data?.exercicios ?? []).map((e) => e.ano)}
          />
        </CampoCompacto>

        <CampoCompacto rotulo="Centro de custo">
          <select
            value={ccFiltro}
            onChange={(e) => setCcFiltro(e.target.value)}
            aria-label="Centro de custo"
            className="max-w-[220px] rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px]"
          >
            <option value="todos">Todos</option>
            {grade.map((rs) => (
              <option key={rs[0]!.cc.id} value={rs[0]!.cc.id}>
                {rs[0]!.cc.codigo} · {rs[0]!.cc.nome}
              </option>
            ))}
          </select>
        </CampoCompacto>
      </FiltrosRecolhiveis>

      {aviso && (
        <p role="status" className="mb-4 rounded-lg border border-ok-linha bg-ok-bg px-3 py-2 text-[13px] text-ok-tinta">
          {aviso}
        </p>
      )}

      {q.isPending && <p className="text-[13px] text-tinta-fraca">Carregando…</p>}
      {q.isError && <Vazio titulo="Não foi possível carregar a planilha" dica={(q.error as FalhaApi).message} />}

      {q.data && visiveis.length === 0 && (
        <Painel>
          <Vazio
            titulo={doCc.length > 0 ? "Nada com valor nos meses da tela" : `Ainda não há orçamento em ${ano}`}
            dica={doCc.length > 0
              ? "Fora da edição só aparecem as linhas com valor nos meses da tela. Troque o mês, ou clique em Editar orçamento para ver e preencher todas."
              : podeEditar
                ? "Clique em Editar orçamento e depois em Adicionar linha — ou traga de uma planilha pela aba Importar."
                : undefined}
          />
        </Painel>
      )}

      {q.data && visiveis.length > 0 && (
        <CaixaGrade reserva={editando && (pendentes.size > 0 || invalidas.size > 0)}>
          <table
            className={`w-full border-collapse text-[12.5px] ${visao === "ano" ? "min-w-[1320px]" : ""}`}
          >
            <thead className="sticky top-0 z-20 bg-superficie-2">
              <tr className="text-left text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
                <th className="sticky left-0 z-30 bg-superficie-2 px-3 py-2">
                  <NomeNaLargura largura={coluna.largura}>Centro de custo · categoria</NomeNaLargura>
                  <AlcaLargura coluna={coluna} />
                </th>
                {colunas.map((m) => <th key={m} className="px-1.5 py-2 text-right">{MESES[m]}</th>)}
                <th className="px-3 py-2 text-right" title="Total do exercício, independente das colunas visíveis">
                  Total do ano
                </th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((rs) => {
                const cc = rs[0]!.cc;
                const subtotal = MESES.map((_, m) => somar(rs.map((r) => valorCat(r, m))));
                const ccAberto = !recolhidos.fechado(cc.id);
                return (
                  <Fragment key={cc.id}>
                    <tr className="border-t-2 border-linha-forte bg-superficie-2/60 font-semibold">
                      <td className="sticky left-0 z-10 bg-superficie-2 px-3 py-2">
                        <NomeNaLargura largura={coluna.largura}>
                          <BotaoGrupo aberto={ccAberto} onClick={() => recolhidos.alternar(cc.id)} rotulo={`${cc.codigo} ${cc.nome}`} />
                          <span className="truncate" title={`${cc.codigo} · ${cc.nome}`}>{cc.codigo} · {cc.nome}</span>
                          {!ccAberto && <span className="shrink-0 text-[11px] font-normal text-tinta-fraca">({rs.length})</span>}
                        </NomeNaLargura>
                      </td>
                      {colunas.map((m) => <td key={m} className="num px-1.5 py-2 text-right">{curto(subtotal[m]!)}</td>)}
                      <td className="num px-3 py-2 text-right">{curto(somar(subtotal))}</td>
                    </tr>
                    {ccAberto && rs.map((r) => {
                      const aberta = abertas.has(r.chave) || editando;
                      const valores = MESES.map((_, m) => valorCat(r, m));
                      return (
                        <Fragment key={r.chave}>
                          <tr className="group border-t border-linha hover:bg-superficie-2/40">
                            <td className="sticky left-0 z-10 bg-superficie px-3 py-1.5">
                              <NomeNaLargura largura={coluna.largura}>
                                {r.subs.length > 0 ? (
                                  <button type="button" onClick={() => alternar(r.chave)}
                                    aria-expanded={aberta} aria-label={aberta ? "Recolher subcategorias" : "Ver subcategorias"}
                                    className="grid size-5 place-items-center rounded-lg text-tinta-fraca hover:bg-superficie-2">
                                    {aberta ? "▾" : "▸"}
                                  </button>
                                ) : <span className="size-5" />}
                                <span className="num text-tinta-fraca">{r.cat.codigo}</span>
                                <span className="truncate" title={r.cat.nome}>{r.cat.nome}</span>
                                {/* O "+ renovação" da categoria só aparece sem subcategoria: com
                                    elas, cada uma é um contrato com a sua data. */}
                                {!editando && (renovacaoDe(r, null) || r.subs.length === 0) && (
                                  <MarcaRenovacao reg={renovacaoDe(r, null)} podeEditar={podeEditarPapel}
                                    onAbrir={() => abrirRenovacao(r, null)} />
                                )}
                                {editando && (
                                  <span className="ml-auto flex shrink-0 gap-1">
                                    <MiniBotao titulo="Adicionar subcategoria" onClick={() => setNovaSubEm(r.chave)}>+ sub</MiniBotao>
                                    <MiniBotao titulo="Tirar os valores da linha" onClick={() => zerarLinha(r)}>limpar</MiniBotao>
                                  </span>
                                )}
                              </NomeNaLargura>
                            </td>
                            {colunas.map((m) => {
                              const v = valores[m]!;
                              const chave = chaveValor(r, m);
                              return editavelCat(r, m) ? (
                                <td key={m} className="px-0.5 py-0.5">
                                  <CelulaEditavel valor={v} textoInvalido={invalidas.get(chave)}
                                    mudou={pendentes.has(chave)} onConfirmar={(t) => digitarCat(r, m, t)} />
                                </td>
                              ) : (
                                <td key={m} className={`num px-1.5 py-1.5 text-right ${editando ? "text-tinta-fraca" : ""}`}
                                  title={r.meses[m]!.length > 1 ? "Mais de uma linha neste mês — edite pela aba Lançar" : editando && v ? "Soma das subcategorias" : undefined}>
                                  {curto(v)}{r.meses[m]!.length > 1 && "*"}
                                </td>
                              );
                            })}
                            <td className="num px-3 py-1.5 text-right font-semibold">{curto(somar(valores))}</td>
                          </tr>
                          {aberta && r.subs.map((s) => {
                            const vs = MESES.map((_, m) => valorSub(s, m));
                            return (
                              <tr key={s.chave} className="group border-t border-linha/60 text-tinta-media">
                                <td className="sticky left-0 z-10 bg-superficie py-1 pr-3 pl-12">
                                  {/* pl-12 em vez de px-3: tira a diferença para a coluna terminar no mesmo lugar. */}
                                  <NomeNaLargura largura={coluna.largura - 36} className="gap-1">
                                    <span className="truncate" title={s.nome}>↳ {s.nome}</span>
                                    {s.pessoa && !editando && <MarcaPessoa pessoa={s.pessoa} />}
                                    {!editando && (
                                      <MarcaRenovacao reg={renovacaoDe(r, s.nome)} podeEditar={podeEditarPapel}
                                        onAbrir={() => abrirRenovacao(r, s.nome)} />
                                    )}
                                    {editando && (
                                      <span className="ml-auto flex shrink-0">
                                        <MiniBotao titulo="Mudar o nome desta subcategoria em todos os meses" onClick={() => abrirRenomear(r, s)}>renomear</MiniBotao>
                                        <MiniBotao titulo={s.pessoa ? `Ligada a ${s.pessoa.nome ?? "uma pessoa"} no Conta Azul: trocar ou desligar` : "Ligar esta subcategoria a uma pessoa ou fornecedor do Conta Azul"} onClick={() => abrirPessoa(r, s)}>{s.pessoa ? "pessoa ✓" : "pessoa"}</MiniBotao>
                                      </span>
                                    )}
                                  </NomeNaLargura>
                                </td>
                                {colunas.map((m) => {
                                  const v = vs[m]!;
                                  const chave = chaveSub(s, m);
                                  return editavelSub(r, s, m) ? (
                                    <td key={m} className="px-0.5 py-0.5">
                                      <CelulaEditavel valor={v} textoInvalido={invalidas.get(chave)}
                                        mudou={pendentes.has(chave)} onConfirmar={(t) => digitarSub(r, s, m, t)} />
                                    </td>
                                  ) : (
                                    <td key={m} className="num px-1.5 py-1 text-right">{curto(v)}</td>
                                  );
                                })}
                                <td className="num px-3 py-1 text-right">{curto(somar(vs))}</td>
                              </tr>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
            {/* O total fica preso no pé da caixa, como o cabeçalho no topo. */}
            <tfoot className="sticky bottom-0 z-20">
              <tr className="border-t-2 border-azul bg-superficie-2 font-bold">
                <td className="sticky left-0 z-10 bg-superficie-2 px-3 py-2">
                  <NomeNaLargura largura={coluna.largura}>Total</NomeNaLargura>
                </td>
                {colunas.map((m) => <td key={m} className="num px-1.5 py-2 text-right">{curto(totalGeral[m]!)}</td>)}
                <td className="num px-3 py-2 text-right">{curto(somar(totalGeral))}</td>
              </tr>
            </tfoot>
          </table>
        </CaixaGrade>
      )}

      {editando && (pendentes.size > 0 || invalidas.size > 0) && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-linha bg-superficie shadow-[0_-4px_12px_rgba(16,40,80,0.08)]">
          <div className="pagina flex flex-wrap items-center gap-3 py-3">
            <span className="text-[13px] font-semibold">
              {pendentes.size} {pendentes.size === 1 ? "alteração" : "alterações"} não salva{pendentes.size === 1 ? "" : "s"}
            </span>
            {invalidas.size > 0 && (
              <span className="text-[12.5px] text-risco-tinta">
                {invalidas.size} célula(s) com valor que não deu para ler — use 1.234,56
              </span>
            )}
            <span className="ml-auto flex gap-2">
              <Botao onClick={descartar}>Descartar</Botao>
              <button type="button" disabled={pendentes.size === 0 || invalidas.size > 0}
                onClick={() => setSalvando(true)}
                className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
                Salvar…
              </button>
            </span>
          </div>
        </div>
      )}

      {salvando && (
        <ModalSalvar
          ano={ano}
          status={status ?? ""}
          pendentes={[...pendentes.values()]}
          onFechar={() => setSalvando(false)}
          onSalvo={async (r) => {
            // As subcategorias novas com pessoa só existem no banco agora.
            const falhasPessoa = await ligarExtras();
            // A planilha recarrega ANTES de limpar o que estava pendente. Na
            // primeira versão limpava primeiro, e a grade ficava vazia alguns
            // segundos — as linhas novas só existiam no estado local. As outras
            // telas (Visão Financeiro, Painel) recarregam depois, sem segurar.
            await qc.refetchQueries({ queryKey: ["planilha", ano] });
            setSalvando(false);
            descartar();
            setAviso(`${r.alteradas} ${r.alteradas === 1 ? "alteração salva" : "alterações salvas"}.` + (r.aviso ? ` ${r.aviso}` : "")
              + (falhasPessoa.length ? ` Não foi possível ligar a pessoa de: ${falhasPessoa.join("; ")}.` : ""));
            void qc.invalidateQueries({ predicate: (x) => x.queryKey[0] !== "planilha" });
          }}
        />
      )}

      {novaSubEm && (
        <ModalNovaSub
          ano={ano}
          linha={novaSubEm}
          todos={cadastros.data?.fornecedores ?? []}
          onFechar={() => setNovaSubEm(null)}
          onCriar={(nome, pessoa) => {
            setSubsExtras((x) => [...x, { linha: novaSubEm, nome, pessoa }]);
            setAbertas((a) => new Set(a).add(novaSubEm));
            setNovaSubEm(null);
          }}
        />
      )}

      {renovando && <ModalRenovacao alvo={renovando} onFechar={() => setRenovando(null)} />}

      {ligando && (
        <ModalPessoaSub
          ano={ano}
          linha={ligando.r}
          sub={ligando.s}
          todos={cadastros.data?.fornecedores ?? []}
          onFechar={() => setLigando(null)}
          onSoNaTela={(pessoa) => { pessoaExtra(ligando.r, ligando.s, pessoa); setLigando(null); }}
          onFeito={async (pessoa, r) => {
            await qc.refetchQueries({ queryKey: ["planilha", ano] });
            setLigando(null);
            if (!r.mudou) { setAviso("Nada mudou: a subcategoria já estava assim."); return; }
            setAviso(pessoa
              ? `“${ligando.s.nome}” ligada a ${pessoa.nome ?? "a pessoa escolhida"}. O pagamento dela passa a cair aqui sozinho.`
                + (r.alocacoes ? ` ${r.alocacoes} ${r.alocacoes === 1 ? "lançamento que já tinha entrado foi" : "lançamentos que já tinham entrado foram"} para a subcategoria.` : "")
              : `“${ligando.s.nome}” não tem mais pessoa ligada.`);
            void qc.invalidateQueries({ predicate: (x) => x.queryKey[0] !== "planilha" });
          }}
        />
      )}

      {renomeando && (
        <ModalRenomearSub
          ano={ano}
          linha={renomeando.r}
          sub={renomeando.s}
          onFechar={() => setRenomeando(null)}
          onRenomeado={async (nome, r) => {
            renomearExtra(renomeando.r, renomeando.s, nome);
            if (r) await qc.refetchQueries({ queryKey: ["planilha", ano] });
            setRenomeando(null);
            if (r && !r.mudou) { setAviso("Nada mudou: a subcategoria já tinha esse nome."); return; }
            setAviso([
              `Subcategoria renomeada para “${nome}”.`,
              r?.regras ? "A regra de aplicar aos iguais acompanhou o nome novo." : null,
              r?.renovacoes ? "A renovação do contrato também." : null,
            ].filter(Boolean).join(" "));
            if (r) void qc.invalidateQueries({ predicate: (x) => x.queryKey[0] !== "planilha" });
          }}
        />
      )}

      {adicionando && (
        <ModalAdicionar
          existentes={new Set(grade.flat().map((r) => r.chave))}
          centros={cadastros.data?.centros ?? []}
          /* ⚠️ Só as categorias da natureza que a grade está mostrando. Sem
             isto, quem está na aba "Despesa e investimento" poderia escolher
             uma categoria de receita e a linha recém-criada sumiria na hora,
             filtrada pela própria grade — parecendo que o botão não funcionou. */
          categorias={(cadastros.data?.categorias ?? []).filter(
            (c) => (c.natureza === "receita") === (natureza === "receita"),
          )}
          onFechar={() => setAdicionando(false)}
          onAdicionar={(x) => {
            setExtras((e) => [...e, x]);
            setAbertas((a) => new Set(a).add(`${x.cc}|${x.cat}`));
            setAdicionando(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * A renovação manual do contrato, ao lado do nome (050). Com data: a pílula,
 * que o Financeiro clica para alterar. Sem data: "+ renovação", que só aparece
 * ao passar o mouse — senão a grade de 428 linhas vira uma coluna de botões.
 */
function MarcaRenovacao({ reg, podeEditar, onAbrir }: {
  reg: { proxima_renovacao: string; observacao: string | null } | undefined;
  podeEditar: boolean;
  onAbrir: () => void;
}) {
  if (reg) {
    const texto = `renova ${dataRenovacao(reg.proxima_renovacao)}`;
    const dica = `Renovação manual${reg.observacao ? ` · ${reg.observacao}` : ""}`;
    const classe = "ml-1 shrink-0 rounded-full border border-atencao-linha bg-atencao-bg px-1.5 py-px text-[10.5px] font-semibold whitespace-nowrap text-atencao-tinta";
    return podeEditar
      ? <button type="button" onClick={onAbrir} title={`${dica} — clique para alterar`} className={`${classe} hover:brightness-95`}>{texto}</button>
      : <span title={dica} className={classe}>{texto}</span>;
  }
  if (!podeEditar) return null;
  return (
    <button type="button" onClick={onAbrir} title="Informar a renovação manual deste contrato"
      className="ml-1 shrink-0 rounded-full px-1.5 py-px text-[10.5px] whitespace-nowrap text-tinta-fraca opacity-0 group-hover:opacity-100 hover:bg-superficie-2 focus:opacity-100">
      + renovação
    </button>
  );
}

/** Valor sem "R$" e sem centavos quando redondo: a grade tem 13 colunas. */
function curto(centavos: string | null): string {
  if (!centavos || BigInt(centavos) === 0n) return "";
  const texto = moeda(centavos).replace("R$ ", "");
  return texto.endsWith(",00") ? texto.slice(0, -3) : texto;
}

function CelulaEditavel({
  valor, textoInvalido, mudou, onConfirmar,
}: {
  valor: string | null;
  textoInvalido?: string;
  mudou: boolean;
  onConfirmar: (texto: string) => void;
}) {
  const [texto, setTexto] = useState<string | null>(null);
  const exibido = texto ?? textoInvalido ?? deCentavos(valor);
  return (
    <input
      type="text"
      inputMode="decimal"
      value={exibido}
      onFocus={() => setTexto(exibido)}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => { onConfirmar(texto ?? exibido); setTexto(null); }}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
      aria-invalid={!!textoInvalido}
      className={`num w-full rounded-lg border px-1.5 py-1 text-right text-[12.5px] outline-none focus:border-laranja ${
        textoInvalido ? "border-risco-linha bg-risco-bg text-risco-tinta"
        : mudou ? "border-laranja/60 bg-laranja/10" : "border-transparent bg-superficie-2/50 hover:border-linha"
      }`}
    />
  );
}

function ModalSalvar({
  ano, status, pendentes, onFechar, onSalvo,
}: {
  ano: number;
  status: string;
  pendentes: Pendente[];
  onFechar: () => void;
  onSalvo: (r: { alteradas: number; aviso?: string | null }) => Promise<void>;
}) {
  const [justificativa, setJustificativa] = useState("");
  const exige = status === "vigente";
  const salvar = useMutation({
    mutationFn: () => api.post<{ alteradas: number; aviso?: string | null }>("/api/planilha", {
      exercicio_ano: ano,
      justificativa: justificativa.trim() || null,
      alteracoes: pendentes.map((p) => p.alteracao),
    }),
    // Devolver a promessa mantém o botão em "Salvando…" até a grade recarregar:
    // sem isso, dava para clicar Salvar de novo no intervalo.
    onSuccess: (r) => onSalvo(r),
  });
  const antes = somar(pendentes.map((p) => p.original));
  const depois = somar(pendentes.map((p) => p.alteracao.valor_centavos));
  const podeSalvar = (!exige || justificativa.trim().length >= 10) && !salvar.isPending;

  return (
    <Modal titulo={`Salvar alterações no orçamento de ${ano}`} onFechar={onFechar}>
      <dl className="grid gap-1 rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px]">
        <div className="flex justify-between"><dt className="text-tinta-fraca">Células alteradas</dt><dd className="num font-medium">{pendentes.length}</dd></div>
        <div className="flex justify-between"><dt className="text-tinta-fraca">Nessas células, antes</dt><dd className="num font-medium">{moeda(antes)}</dd></div>
        <div className="flex justify-between"><dt className="text-tinta-fraca">Nessas células, depois</dt><dd className="num font-medium">{moeda(depois)}</dd></div>
      </dl>

      <label className="mt-3 block">
        <span className="text-[12px] font-semibold text-tinta-media">
          Justificativa{exige && <span className="text-laranja-tinta"> *</span>}
        </span>
        <textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} rows={3}
          placeholder={exige ? "Por que o orçamento deste exercício está mudando, e quem aprovou." : "Opcional no rascunho."}
          className="mt-1 w-full resize-y rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]" />
        <span className="mt-1 block text-[11.5px] text-tinta-fraca">
          {exige
            ? `${ano} está valendo: a justificativa é obrigatória (ao menos 10 caracteres) e fica no Histórico como o registro da revisão.`
            : "Fica no Histórico junto com o antes e o depois de cada célula."}
        </span>
      </label>

      {salvar.isError && (
        <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(salvar.error as FalhaApi).message}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Botao onClick={onFechar}>Voltar</Botao>
        <button type="button" disabled={!podeSalvar} onClick={() => salvar.mutate()}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          {salvar.isPending ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}

function ModalAdicionar({
  existentes, centros, categorias, onFechar, onAdicionar,
}: {
  existentes: Set<string>;
  centros: Cadastros["centros"];
  categorias: Cadastros["categorias"];
  onFechar: () => void;
  onAdicionar: (x: { cc: string; cat: string; tipo: string }) => void;
}) {
  const [cc, setCc] = useState("");
  const [cat, setCat] = useState("");
  const [tipo, setTipo] = useState("pontual");
  // Só categoria do último nível recebe orçamento (v_categoria_orcavel, 012):
  // um grupo do plano de contas seria recusado na hora de salvar.
  const temFilho = new Set(categorias.map((c) => c.pai_id).filter(Boolean));
  const folhas = categorias.filter((c) => !temFilho.has(c.id));
  const jaExiste = !!cc && !!cat && existentes.has(`${cc}|${cat}`);

  return (
    <Modal titulo="Adicionar linha ao orçamento" onFechar={onFechar}>
      <label className="block">
        <span className="text-[12px] font-semibold text-tinta-media">Centro de custo</span>
        <select value={cc} onChange={(e) => setCc(e.target.value)}
          className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]">
          <option value="">Escolha…</option>
          {centros.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>)}
        </select>
      </label>
      <label className="mt-3 block">
        <span className="text-[12px] font-semibold text-tinta-media">Categoria</span>
        <select value={cat} onChange={(e) => setCat(e.target.value)}
          className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]">
          <option value="">Escolha…</option>
          {folhas.map((c) => <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} · ` : ""}{c.nome}</option>)}
        </select>
      </label>
      <label className="mt-3 block">
        <span className="text-[12px] font-semibold text-tinta-media">Tipo</span>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)}
          className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]">
          <option value="pontual">Pontual / ação</option>
          <option value="recorrente">Recorrente (mensal)</option>
          <option value="investimento">Investimento</option>
        </select>
      </label>
      {jaExiste && (
        <p className="mt-3 text-[12.5px] text-atencao-tinta">
          Essa combinação já está na planilha — edite os meses dela direto na grade.
        </p>
      )}
      <p className="mt-3 text-[12px] text-tinta-fraca">
        A linha aparece vazia na grade; preencha os meses e salve.
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <Botao onClick={onFechar}>Cancelar</Botao>
        <button type="button" disabled={!cc || !cat || jaExiste} onClick={() => onAdicionar({ cc, cat, tipo })}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          Adicionar
        </button>
      </div>
    </Modal>
  );
}

function ModalNovaSub({
  ano, linha, todos, onFechar, onCriar,
}: {
  ano: number;
  /** A chave da linha da grade: `CC|categoria`. */
  linha: string;
  todos: Pessoa[];
  onFechar: () => void;
  onCriar: (nome: string, pessoa?: Pessoa) => void;
}) {
  const [nome, setNome] = useState("");
  const [comPessoa, setComPessoa] = useState(false);
  const [pessoa, setPessoa] = useState<Pessoa | null>(null);
  const [cc, cat] = linha.split("|") as [string, string];
  const final = nome.trim() || (comPessoa && pessoa?.nome ? pessoa.nome.trim() : "");
  const pode = !!final && (!comPessoa || !!pessoa);
  const criar = () => { if (pode) onCriar(final, comPessoa && pessoa ? pessoa : undefined); };
  return (
    <Modal titulo="Nova subcategoria" onFechar={onFechar}>
      <label className="flex items-center gap-2 text-[13px] text-tinta-media">
        <input type="checkbox" checked={comPessoa} onChange={(e) => setComPessoa(e.target.checked)} />
        É uma pessoa ou um fornecedor (orçar por pessoa)
      </label>
      {comPessoa && (
        <div className="mt-3">
          <EscolherPessoa ano={ano} cc={cc} cat={cat} todos={todos} escolhida={pessoa} onEscolher={setPessoa} />
        </div>
      )}
      <label className="mt-4 block">
        <span className="text-[12px] font-semibold text-tinta-media">Nome{comPessoa ? " (opcional)" : ""}</span>
        <input autoFocus={!comPessoa} value={nome} onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") criar(); }}
          placeholder={comPessoa ? pessoa?.nome ?? "O nome da pessoa, se ficar vazio" : "Ex.: Ovo de Páscoa, Presente de fim de ano"}
          className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]" />
        <span className="mt-1 block text-[11.5px] text-tinta-fraca">
          {comPessoa
            ? "O pagamento dessa pessoa no Conta Azul cai nesta subcategoria sozinho, sem pedir confirmação. A ligação vale depois de salvar."
            : "Use o nome que o Financeiro escreve na descrição do lançamento: é por ele que o sistema sugere a qual subcategoria o gasto pertence."}
        </span>
      </label>
      <div className="mt-5 flex justify-end gap-2">
        <Botao onClick={onFechar}>Cancelar</Botao>
        <button type="button" disabled={!pode} onClick={criar}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          Adicionar
        </button>
      </div>
    </Modal>
  );
}

/** A marca da subcategoria ligada a uma pessoa ou fornecedor (080). */
function MarcaPessoa({ pessoa }: { pessoa: Pessoa }) {
  return (
    <span
      className="shrink-0 rounded-full bg-azul/10 px-1.5 py-px text-[10.5px] font-semibold text-azul"
      title={`Ligada a ${pessoa.nome ?? "uma pessoa"} no Conta Azul: o pagamento cai aqui sozinho`}
    >
      pessoa
    </span>
  );
}

/**
 * Escolher a pessoa ou fornecedor do Conta Azul. Sem busca, a lista é de
 * quem recebeu nesta categoria e centro de custo no ano (e no anterior);
 * com busca, o cadastro inteiro.
 */
function EscolherPessoa({
  ano, cc, cat, todos, escolhida, onEscolher,
}: {
  ano: number;
  cc: string;
  cat: string;
  todos: Pessoa[];
  escolhida: Pessoa | null;
  onEscolher: (p: Pessoa) => void;
}) {
  const [busca, setBusca] = useState("");
  const recebeu = useQuery({
    queryKey: ["planilha", "quem-recebeu", ano, cc, cat],
    queryFn: async () => {
      const [este, anterior] = await Promise.all([
        buscarRealizadoPorFornecedor(ano, cc, cat),
        buscarRealizadoPorFornecedor(ano - 1, cc, cat),
      ]);
      const vistos = new Map<string, Pessoa>();
      for (const l of [...este.linhas, ...anterior.linhas]) {
        if (l.fornecedor_id && !vistos.has(l.fornecedor_id)) vistos.set(l.fornecedor_id, { id: l.fornecedor_id, nome: l.fornecedor_nome });
      }
      return [...vistos.values()].sort((a, b) => (a.nome ?? "").localeCompare(b.nome ?? ""));
    },
  });
  const termo = normalizar(busca);
  const lista = termo
    ? todos.filter((p) => normalizar(p.nome ?? "").includes(termo)).slice(0, 30)
    : recebeu.data ?? [];
  const carregando = !termo && recebeu.isPending;
  return (
    <div>
      <input value={busca} onChange={(e) => setBusca(e.target.value)} autoFocus
        placeholder="Buscar pelo nome no Conta Azul"
        className="w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]" />
      <span className="mt-1 block text-[11.5px] text-tinta-fraca">
        {termo ? "Cadastro inteiro de fornecedores do Conta Azul." : "Quem recebeu nesta categoria e centro de custo, neste ano e no anterior."}
      </span>
      <ul className="thin-scroll mt-2 max-h-56 overflow-y-auto rounded-lg border border-linha">
        {carregando && <li className="px-3 py-2 text-[12.5px] text-tinta-fraca">Carregando…</li>}
        {!carregando && lista.length === 0 && (
          <li className="px-3 py-2 text-[12.5px] text-tinta-fraca">
            {termo ? "Ninguém com esse nome no cadastro." : "Ninguém recebeu aqui ainda: busque pelo nome."}
          </li>
        )}
        {lista.map((p) => (
          <li key={p.id}>
            <button type="button" onClick={() => onEscolher(p)}
              className={`block w-full px-3 py-1.5 text-left text-[13px] hover:bg-superficie-2 ${escolhida?.id === p.id ? "bg-azul/10 font-semibold text-azul" : ""}`}>
              {p.nome ?? "(sem nome)"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

type RespostaVincular = { mudou: boolean; alocacoes?: number };

/**
 * Ligar a subcategoria a uma pessoa ou fornecedor (080). Salva na hora, nos
 * doze meses do exercício. A subcategoria que só existe na tela guarda a
 * pessoa, e é ligada quando a edição for salva.
 */
function ModalPessoaSub({
  ano, linha, sub, todos, onFechar, onSoNaTela, onFeito,
}: {
  ano: number;
  linha: LinhaCat;
  sub: LinhaSub;
  todos: Pessoa[];
  onFechar: () => void;
  onSoNaTela: (pessoa: Pessoa | undefined) => void;
  onFeito: (pessoa: Pessoa | null, r: RespostaVincular) => Promise<void>;
}) {
  const [pessoa, setPessoa] = useState<Pessoa | null>(sub.pessoa);
  const soNaTela = sub.meses.every((c) => c === null);
  const ligar = useMutation({
    mutationFn: (p: Pessoa | null) => api.post<RespostaVincular>("/api/planilha/vincular-subcategoria", {
      exercicio_ano: ano, centro_custo_id: linha.cc.id, categoria_id: linha.cat.id, nome: sub.nome, fornecedor_id: p?.id ?? null,
    }),
    onSuccess: (r, p) => onFeito(p, r),
  });
  const aplicar = (p: Pessoa | null) => {
    if (soNaTela) onSoNaTela(p ?? undefined);
    else ligar.mutate(p);
  };
  return (
    <Modal titulo="Pessoa da subcategoria" onFechar={onFechar}>
      <p className="text-[13px] text-tinta-media">
        <strong>{sub.nome}</strong> em {linha.cat.nome}, centro de custo {linha.cc.codigo}.
        {sub.pessoa ? ` Hoje ligada a ${sub.pessoa.nome ?? "uma pessoa"}.` : " Hoje sem pessoa ligada."}
      </p>
      <p className="mt-1 text-[12px] text-tinta-fraca">
        Com a pessoa ligada, o pagamento dela no Conta Azul cai nesta subcategoria sozinho, sem pedir confirmação, nos doze meses de {ano}.
      </p>
      <div className="mt-3">
        <EscolherPessoa ano={ano} cc={linha.cc.id} cat={linha.cat.id} todos={todos} escolhida={pessoa} onEscolher={setPessoa} />
      </div>
      {ligar.isError && (
        <p className="mt-3 text-[12.5px] text-risco-tinta">{(ligar.error as FalhaApi).message}</p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        {sub.pessoa && <Botao onClick={() => aplicar(null)}>Desligar</Botao>}
        <Botao onClick={onFechar}>Cancelar</Botao>
        <button type="button" disabled={!pessoa || pessoa.id === sub.pessoa?.id || ligar.isPending} onClick={() => aplicar(pessoa)}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          {ligar.isPending ? "Salvando…" : "Ligar"}
        </button>
      </div>
    </Modal>
  );
}

type RespostaRenomear = { mudou: boolean; regras?: number; renovacoes?: number };

/**
 * Renomear a subcategoria (076). Salva na hora, nos doze meses do exercício.
 * A subcategoria acrescentada nesta edição e ainda sem valor não existe no
 * banco: muda só na tela, sem chamar a API.
 */
function ModalRenomearSub({
  ano, linha, sub, onFechar, onRenomeado,
}: {
  ano: number;
  linha: LinhaCat;
  sub: LinhaSub;
  onFechar: () => void;
  onRenomeado: (nome: string, r: RespostaRenomear | null) => Promise<void>;
}) {
  const [nome, setNome] = useState(sub.nome);
  const [motivo, setMotivo] = useState("");
  const limpo = nome.trim().replace(/\s+/g, " ");
  const soNaTela = sub.meses.every((c) => c === null);
  const meses = sub.meses.filter(Boolean).length;
  // A mesma recusa da RPC, avisada antes: juntar duas subcategorias não é renomear.
  const repetido = linha.subs.some((x) => x !== sub && normalizar(x.nome) === normalizar(limpo));
  const renomear = useMutation({
    mutationFn: () => api.post<RespostaRenomear>("/api/planilha/renomear-subcategoria", {
      exercicio_ano: ano,
      centro_custo_id: linha.cc.id,
      categoria_id: linha.cat.id,
      nome_atual: sub.nome,
      nome_novo: limpo,
      motivo: motivo.trim() || null,
    }),
    // Devolver a promessa mantém o botão em "Salvando…" até a grade recarregar.
    onSuccess: (r) => onRenomeado(limpo, r),
  });
  const podeSalvar = !!limpo && limpo !== sub.nome && !repetido && limpo.length <= 200 && !renomear.isPending;
  const salvar = () => {
    if (!podeSalvar) return;
    if (soNaTela) void onRenomeado(limpo, null);
    else renomear.mutate();
  };

  return (
    <Modal titulo="Renomear subcategoria" onFechar={onFechar}>
      <p className="text-[12.5px] text-tinta-fraca">
        {linha.cc.codigo} · {linha.cc.nome} › {linha.cat.codigo ? `${linha.cat.codigo} ` : ""}{linha.cat.nome}
      </p>
      <label className="mt-3 block">
        <span className="text-[12px] font-semibold text-tinta-media">Nome</span>
        <input autoFocus value={nome} onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") salvar(); }}
          maxLength={200}
          className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]" />
        <span className="mt-1 block text-[11.5px] text-tinta-fraca">
          {soNaTela
            ? "Esta subcategoria ainda não foi salva: o nome muda só aqui, e vai junto quando você salvar os valores."
            : `Muda o nome em ${meses === 1 ? "1 mês" : `${meses} meses`} de ${ano}, de uma vez. Nenhum valor muda, e os lançamentos já ligados a ela continuam ligados.`}
          {" "}O sistema sugere a subcategoria comparando este nome com a descrição do lançamento no Conta Azul.
        </span>
      </label>
      {repetido && (
        <p className="mt-2 text-[12.5px] text-atencao-tinta">
          Já existe uma subcategoria com esse nome nesta categoria. Para juntar as duas, passe o valor para a que fica e tire a outra.
        </p>
      )}
      {!soNaTela && (
        <label className="mt-3 block">
          <span className="text-[12px] font-semibold text-tinta-media">Motivo (opcional)</span>
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: nome igual ao da descrição no Conta Azul"
            className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]" />
          <span className="mt-1 block text-[11.5px] text-tinta-fraca">
            O nome de antes e o novo ficam no Histórico.
          </span>
        </label>
      )}

      {renomear.isError && (
        <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(renomear.error as FalhaApi).message}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Botao onClick={onFechar}>Cancelar</Botao>
        <button type="button" disabled={!podeSalvar} onClick={salvar}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          {renomear.isPending ? "Salvando…" : "Renomear"}
        </button>
      </div>
    </Modal>
  );
}

function Botao({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className="rounded-lg border border-linha px-3 py-1.5 text-[12.5px] font-semibold whitespace-nowrap hover:border-laranja hover:text-laranja-tinta">
      {children}
    </button>
  );
}

function MiniBotao({ onClick, titulo, children }: { onClick: () => void; titulo: string; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} title={titulo}
      className="rounded-lg border border-linha px-1.5 py-0.5 text-[10.5px] font-semibold text-tinta-fraca hover:border-laranja hover:text-laranja-tinta">
      {children}
    </button>
  );
}

